/**
 * Medidas sobre texto que usan varias capas del lector.
 *
 * Están acá y no adentro de una de ellas porque las tres las necesitan por el
 * mismo motivo: el OCR no devuelve lo que está impreso, devuelve algo parecido,
 * y decidir cuánto es "parecido" no puede quedar escrito en tres lugares
 * distintos con tres criterios distintos.
 */

/**
 * Distancia de edición. Corta apenas se pasa del tope, que es lo único que
 * necesitamos saber.
 */
export function distancia(a: string, b: string, tope: number): number {
  if (Math.abs(a.length - b.length) > tope) return tope + 1;
  let previa = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const actual = [i];
    let mejorDeLaFila = i;
    for (let j = 1; j <= b.length; j++) {
      const costo = a[i - 1] === b[j - 1] ? 0 : 1;
      const v = Math.min(previa[j] + 1, actual[j - 1] + 1, previa[j - 1] + costo);
      actual.push(v);
      if (v < mejorDeLaFila) mejorDeLaFila = v;
    }
    if (mejorDeLaFila > tope) return tope + 1;
    previa = actual;
  }
  return previa[b.length];
}

/**
 * Un texto impreso, sin lo que el OCR le pega al lado.
 *
 * El OCR devuelve comillas, paréntesis y guiones que no están en la tarjeta:
 * medido, el nombre salió `“MARIANA` y el apellido `MARTINEZ £2 .`. A la basura
 * se le deja el espacio que ocupaba —no se pegan dos palabras que estaban
 * separadas— y después se colapsa.
 */
export function textoLimpio(t: string): string {
  return t
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * ¿Son dos lecturas RUIDOSAS DEL MISMO DATO, o dos datos distintos?
 *
 * Una letra cada ocho, y nunca menos de una. La diferencia importa: dos fuentes
 * que dicen cosas distintas son un conflicto y el dato no se sabe; dos fuentes
 * que dicen lo mismo con un carácter de diferencia son el mismo dato leído dos
 * veces con ruido, y declarar eso "en conflicto" borra de la pantalla un nombre
 * que está entero.
 *
 * Medido sobre el frente de 2010: el MRZ dio "RODOLFO MANUEL" y el impreso
 * "RODOLFO MANUEI". Es el mismo nombre.
 */
export function esLaMismaLectura(a: string, b: string): boolean {
  if (a === b) return true;
  const tope = Math.max(1, Math.floor(Math.max(a.length, b.length) / 8));
  return distancia(a, b, tope) <= tope;
}
